import { useQuery } from '@tanstack/react-query';

import VendorsService from '~/services/VendorsService';

import { VendorPromotion, VendorPromotionsPage } from '~/types/services/vendor';

export const vendorPromotionsQueryKeys = {
  all: ['vendor-promotions'] as const,
  list: (vendorId: string, from: string, to: string) => [...vendorPromotionsQueryKeys.all, vendorId, from, to] as const,
};

type Params = {
  vendorId: string | null;
  from?: string;
  to?: string;
  enabled?: boolean;
};

const PAGE_SIZE = 100;

export function useGetVendorPromotions({ vendorId, from = '', to = '', enabled = true }: Params) {
  return useQuery({
    queryKey: vendorPromotionsQueryKeys.list(vendorId ?? '', from, to),
    queryFn: async ({ signal }): Promise<VendorPromotionsPage> => {
      if (!vendorId) throw new Error('vendorId is required');

      const baseParams = { from: from || undefined, to: to || undefined };
      const rows: Array<VendorPromotion> = [];
      let offset = 0;
      let count;

      do {
        const page: VendorPromotionsPage = await VendorsService.getVendorPromotions(
          vendorId,
          { ...baseParams, limit: PAGE_SIZE, offset },
          { signal },
        );
        rows.push(...page.rows);
        count = page.count;
        offset += page.rows.length;
        if (page.rows.length === 0) break;
      } while (rows.length < count);

      return { rows, count };
    },
    enabled: enabled && !!vendorId,
    staleTime: 5 * 60 * 1000,
  });
}
